import React, { Component } from 'react';
import {
    Button,
    ButtonColors,
    ButtonFontWeights,
    ButtonVariants,
    CardVariants,
} from '@clockout-dev/design-elements';
import { DownloadOutlined } from '@ant-design/icons';
import { UsersIcon } from '@heroicons/react/24/outline';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import BillingController from './billing.controller';
import withHooks from '../../core/withHooks';
import Inherit from '../../core/inherit';
import Page from '../common/page/Page';
import { SCOPES } from '../../constants/scopes.constants';
import DashboardPeriodControls from '../dashboard/elements/DashboardPeriodControls';
import BillingKpiCard from './elements/BillingKpiCard';
import BillingStatementsCard from './elements/BillingStatementsCard';
import { BILLING_DEFAULT_TIMEFRAME } from './billing.constants';
import { getDashboardPriorPeriodRangeTooltip } from '../../utils/dates';
import { formatNumberWithCommas } from '../../utils/numbers';
import styles from './billing.module.scss';

class Billing extends Inherit(BillingController, Component) {
    state = {
        user: {},
        loading: true,
        timeFrame: BILLING_DEFAULT_TIMEFRAME,
        kpiData: null,
        kpiLoaded: false,
        statements: [],
        statementsLoaded: false,
        dateRange: null,
        selectedStatements: new Set(),
    };

    handleSelectionChange = (keys) => {
        this.setState({ selectedStatements: new Set(keys) });
    };

    render() {
        const {
            kpiData,
            statements,
            statementsLoaded,
            dateRange,
            timeFrame,
            selectedStatements,
            loading,
        } = this.state;

        const priorPeriodRangeTooltip = getDashboardPriorPeriodRangeTooltip(timeFrame, dateRange);

        const billableUsers = kpiData?.billableUsers ?? 0;
        const billableUsersChange = kpiData?.billableUsersChange;
        const invoiceAmountChange = kpiData?.invoiceAmountChange;
        const nextInvoiceDue = kpiData?.nextInvoiceDue ?? '—';
        const formattedInvoiceAmount = this.getFormattedInvoiceAmount();

        return (
            <Page
                isLoading={loading}
                scope={SCOPES.BILLING_VIEW}
                title={{ text: 'Billing', highlight: 'Hub' }}
                description="Manage your billing information & statements."
                notificationContextHolder={this.props.notificationContextHolder}
                action={
                    <Button
                        size="l"
                        variant={ButtonVariants.PRIMARY}
                        color={ButtonColors.PRIMARY}
                        fontWeight={ButtonFontWeights.SEMIBOLD}
                        className={styles.downloadButton}
                        onClick={() => this.exportToXLSX()}
                    >
                        <DownloadOutlined className={styles.downloadIcon} />
                        Download XLSX
                    </Button>
                }
            >
                <div className={styles.filterRow}>
                    <DashboardPeriodControls
                        timeFrame={timeFrame}
                        dateRange={dateRange}
                        onTimeFrameChange={(v) => this.handleBillingTimeFrameChange(v)}
                        onDateRangeChange={(dates) => this.handleBillingDateRangeChange(dates)}
                    />
                </div>

                <section className={styles.kpiSection}>
                    <BillingKpiCard
                        variant={CardVariants.HIGHLIGHT}
                        IconComponent={UsersIcon}
                        label="Billable Users"
                        value={formatNumberWithCommas(billableUsers)}
                        delta={
                            billableUsersChange != null
                                ? { change: billableUsersChange, tooltip: priorPeriodRangeTooltip }
                                : null
                        }
                    />
                    <BillingKpiCard
                        IconComponent={CheckBadgeIcon}
                        label="Invoice Amount"
                        value={formattedInvoiceAmount}
                        delta={
                            invoiceAmountChange != null
                                ? { change: invoiceAmountChange, tooltip: priorPeriodRangeTooltip }
                                : null
                        }
                    />
                    <BillingKpiCard
                        IconComponent={CheckBadgeIcon}
                        label="Next Invoice Due"
                        value={nextInvoiceDue}
                    />
                </section>

                <BillingStatementsCard
                    statements={statements}
                    loading={!statementsLoaded}
                    selectedStatementIds={selectedStatements}
                    onSelectionChange={this.handleSelectionChange}
                />
            </Page>
        );
    }
}

export default withHooks(Billing);
